import { create } from 'zustand';
import { supabase } from '../config/supabase';

import { PermissionLevel } from '../types';
import { deriveExternalUserFlag } from '../utils/user-scope';

// 用户偏好设置类型
export interface UserPreferences {
  sidebar_width?: number;
  sidebar_collapsed?: boolean;
  theme?: 'light' | 'dark' | 'system';
  language?: string;
  // 未来可扩展更多偏好...
}

export interface UserProfile {
  id: string;
  email: string;
  name: string | null;
  avatar_url: string | null;
  wecom_userid?: string | null;
  role: PermissionLevel;
  created_at: string;
  phone_number: string | null;
  language_preference: string;
  last_password_change: string;
  two_factor_enabled: boolean;
  avatar_storage_path?: string | null;
  preferences?: UserPreferences;
  tenant?: 'cn' | 'intl'; // 用户所属租户
  can_access_all_tenants?: boolean; // 是否可以访问所有租户（超级管理员）
  is_faq_admin?: boolean; // FAQ 管理员标识
  is_external?: boolean; // 外部用户标识
}

interface UserState {
  profile: UserProfile | null;
  preferences: UserPreferences;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchProfile: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  updateAvatar: (file: File) => Promise<void>;
  updatePreferences: (updates: Partial<UserPreferences>) => Promise<void>;
}

// 默认偏好设置
const DEFAULT_PREFERENCES: UserPreferences = {
  sidebar_width: 240,
  sidebar_collapsed: false,
  theme: 'light',
  language: 'zh-CN',
};

async function loadCurrentProfile(userId: string): Promise<UserProfile> {
  const { data: profile, error } = await supabase
    .from('v_fs_users')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) throw error;
  const normalizedProfile = profile as UserProfile;
  return {
    ...normalizedProfile,
    is_external: deriveExternalUserFlag(normalizedProfile.email, normalizedProfile.is_external),
  };
}

function mergePreferences(profile: UserProfile | null): UserPreferences {
  return { ...DEFAULT_PREFERENCES, ...(profile?.preferences || {}) };
}

function normalizeNullableText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function buildAvatarStorageKey(userId: string, filename: string): string {
  const safeName = (filename || 'avatar')
    .replace(/[/\\]/g, '-')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 96) || 'avatar';

  return `${userId}/${Date.now()}-${safeName}`;
}

export const useUserStore = create<UserState>((set, get) => ({
  profile: null,
  preferences: DEFAULT_PREFERENCES,
  isLoading: false,
  error: null,

  fetchProfile: async () => {
    try {
      set({ isLoading: true, error: null });
      
      const { data: authUser } = await supabase.auth.getUser();
      if (!authUser.user) throw new Error('No authenticated user');

      const profile = await loadCurrentProfile(authUser.user.id);
      const mergedPreferences = mergePreferences(profile);
      
      set({ 
        profile,
        preferences: mergedPreferences,
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      set({ error: (error as Error).message });
    } finally {
      set({ isLoading: false });
    }
  },

  updateProfile: async (updates) => {
    try {
      set({ isLoading: true, error: null });
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const coreUpdates: Record<string, string | null> = {};
      if ('name' in updates) coreUpdates.name = normalizeNullableText(updates.name);
      if ('phone_number' in updates) coreUpdates.phone_number = normalizeNullableText(updates.phone_number);
      if ('language_preference' in updates && updates.language_preference) {
        coreUpdates.language_preference = updates.language_preference;
      }
      if ('avatar_url' in updates) coreUpdates.avatar_url = normalizeNullableText(updates.avatar_url);

      if (Object.keys(coreUpdates).length > 0) {
        const { error } = await supabase
          .from('core_users')
          .update(coreUpdates)
          .eq('id', user.id);

        if (error) throw error;
      }

      const fsUpdates: Record<string, UserPreferences | string | null> = {};
      if ('avatar_storage_path' in updates) {
        fsUpdates.avatar_storage_path = updates.avatar_storage_path || null;
      }
      if ('preferences' in updates && updates.preferences) {
        fsUpdates.preferences = updates.preferences;
      }

      if (Object.keys(fsUpdates).length > 0) {
        const { error } = await supabase
          .from('fs_user_profiles')
          .update(fsUpdates)
          .eq('id', user.id);

        if (error) throw error;
      }

      const profile = await loadCurrentProfile(user.id);
      set({
        profile,
        preferences: mergePreferences(profile),
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  updateAvatar: async (file) => {
    try {
      set({ isLoading: true, error: null });
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const storageKey = buildAvatarStorageKey(user.id, file.name);
      
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(storageKey, file, {
          cacheControl: '31536000',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(storageKey);

      const { error: coreError } = await supabase
        .from('core_users')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);
      if (coreError) throw coreError;

      const { error: profileError } = await supabase
        .from('fs_user_profiles')
        .update({ avatar_storage_path: storageKey })
        .eq('id', user.id);
      if (profileError) throw profileError;

      const previousAvatarPath = get().profile?.avatar_storage_path;
      if (previousAvatarPath?.startsWith(`${user.id}/`)) {
        await supabase.storage
          .from('avatars')
          .remove([previousAvatarPath]);
      }

      const profile = await loadCurrentProfile(user.id);
      set({
        profile,
        preferences: mergePreferences(profile),
      });
    } catch (error) {
      console.error('Error updating avatar:', error);
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  // 更新用户偏好设置（防抖调用，自动保存到数据库）
  updatePreferences: async (updates) => {
    try {
      const currentPreferences = get().preferences;
      const newPreferences = { ...currentPreferences, ...updates };
      
      // 立即更新本地状态（乐观更新）
      set({ preferences: newPreferences });
      
      // 同时保存到 localStorage 作为备份
      localStorage.setItem('user-preferences', JSON.stringify(newPreferences));
      
      // 保存到数据库
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.warn('No authenticated user, preferences saved to localStorage only');
        return;
      }

      const { error } = await supabase
        .from('fs_user_profiles')
        .update({ preferences: newPreferences })
        .eq('id', user.id);

      if (error) {
        console.error('Error saving preferences to database:', error);
        // 不抛出错误，因为本地状态已更新
      }
    } catch (error) {
      console.error('Error updating preferences:', error);
    }
  },
}));
